/**
 * Audio utilities for mobile-friendly voice simulation playback
 *
 * These utilities handle iOS audio restrictions and provide reliable
 * playback using HTMLAudioElement with Blob URLs.
 */

import { clientLogger } from './clientLogger';

/**
 * Play MP3 audio from raw bytes using HTMLAudioElement
 * This approach works reliably on iOS Safari, unlike AudioContext.decodeAudioData for MP3
 *
 * @param {ArrayBuffer} arrayBuffer - Raw MP3 audio bytes
 * @returns {Promise<void>} Resolves when audio finishes playing
 */
export async function playMp3Bytes(arrayBuffer) {
  clientLogger.audioDebug('playMp3Bytes_start', {
    bufferSize: arrayBuffer.byteLength,
    bufferType: arrayBuffer.constructor.name
  });
  
  try {
    // Convert ArrayBuffer to base64 data URL (iOS handles this better than blob URLs)
    const base64Audio = btoa(
      new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );
    const dataUrl = `data:audio/mpeg;base64,${base64Audio}`;
    
    clientLogger.audioDebug('data_url_created', {
      dataUrlLength: dataUrl.length
    });

    const audio = new Audio();
    audio.src = dataUrl; // Set source immediately
    audio.playsInline = true;
    audio.preload = "auto";
    
    clientLogger.audioDebug('audio_element_configured', {
      playsInline: audio.playsInline,
      preload: audio.preload
    });

    // Start play() synchronously
    clientLogger.audioDebug('calling_audio_play', {});
    const playPromise = audio.play();
    clientLogger.audioDebug('play_promise_created', {});

    try {
      // Await the play promise
      await playPromise;
      clientLogger.audioDebug('audio_play_started', {});

      // Wait for audio to finish
      await new Promise((resolve, reject) => {
        audio.onended = () => {
          clientLogger.audioDebug('audio_ended_naturally', {});
          resolve();
        };
        audio.onerror = (e) => {
          clientLogger.error('audio_element_error', {
            error: e.target?.error?.message || 'Unknown error',
            errorCode: e.target?.error?.code,
            readyState: audio.readyState,
            networkState: audio.networkState
          });
          reject(new Error('Audio playback failed'));
        };
      });
      
      clientLogger.audioDebug('playMp3Bytes_complete', {});
    } catch (playError) {
      clientLogger.error('audio_play_failed', {
        error: playError.message,
        errorName: playError.name,
        readyState: audio.readyState,
        networkState: audio.networkState,
        paused: audio.paused,
        ended: audio.ended
      });
      throw playError;
    }
  } catch (error) {
    clientLogger.error('playMp3Bytes_failed', {
      error: error.message,
      errorName: error.name,
      errorStack: error.stack
    });
    throw error;
  }
}

/**
 * Play multiple audio segments sequentially
 *
 * @param {Array<ArrayBuffer>} audioBuffers - Array of MP3 audio bytes
 * @param {Function} onSegmentStart - Optional callback called before each segment
 * @returns {Promise<void>} Resolves when all segments finish playing
 */
export async function playAudioSequence(audioBuffers, onSegmentStart) {
  for (let i = 0; i < audioBuffers.length; i++) {
    if (onSegmentStart) {
      onSegmentStart(i);
    }
    await playMp3Bytes(audioBuffers[i]);
  }
}

/**
 * Fetch audio from TTS endpoint and play it
 *
 * @param {string} text - Text to convert to speech
 * @param {string} voice - OpenAI voice name (e.g., 'alloy', 'nova', 'shimmer')
 * @param {string} style - Optional style: "radio" or "clean"
 * @returns {Promise<void>} Resolves when audio finishes playing
 */
export async function speakText(text, voice = "alloy", style = "clean") {
  const response = await fetch("/tts", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ text, voice, style }),
  });

  if (!response.ok) {
    throw new Error(`TTS request failed: ${response.status}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  await playMp3Bytes(arrayBuffer);
}

/**
 * Play tone using WebAudio (for dispatch tones, alerts, etc.)
 * Must be called within user gesture context
 *
 * @param {AudioContext} audioContext - The audio context (must be resumed)
 * @param {number} frequency - Tone frequency in Hz
 * @param {number} duration - Duration in seconds
 * @param {string} type - Oscillator type: 'sine', 'square', 'sawtooth', 'triangle'
 * @returns {Promise<void>} Resolves when tone finishes
 */
export async function playTone(audioContext, frequency, duration, type = "sine") {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.type = type;
  oscillator.frequency.value = frequency;

  // Envelope: fade in and fade out
  const now = audioContext.currentTime;
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01); // Fade in
  gainNode.gain.linearRampToValueAtTime(0, now + duration); // Fade out

  oscillator.start(now);
  oscillator.stop(now + duration);

  // Wait for tone to finish
  return new Promise((resolve) => {
    setTimeout(resolve, duration * 1000);
  });
}

/**
 * Play dispatch alert tones (110 warble)
 * This plays a pager tone followed by a brief pause
 *
 * @param {AudioContext} audioContext - The audio context (must be resumed)
 * @returns {Promise<void>} Resolves when tones finish
 */
export async function playDispatchTones(audioContext) {
  // Two-tone pager: alternating frequencies
  const tone1Freq = 1100; // Hz
  const tone2Freq = 1300; // Hz
  const cycleDuration = 0.25; // seconds per tone
  const cycles = 4; // 4 alternations

  for (let i = 0; i < cycles; i++) {
    await playTone(audioContext, i % 2 === 0 ? tone1Freq : tone2Freq, cycleDuration, "square");
  }

  // Brief pause before dispatch audio
  await new Promise((resolve) => setTimeout(resolve, 300));
}

/**
 * Initialize audio for iOS
 * Must be called from a user gesture (e.g., button click)
 *
 * @returns {AudioContext} The initialized audio context
 */
export async function initializeAudio() {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  await audioContext.resume();
  return audioContext;
}

/**
 * Apply radio distortion effect using WebAudio filters
 * This creates a bandpass filter to simulate radio quality
 *
 * @param {AudioContext} audioContext - The audio context
 * @param {AudioNode} source - The audio source node
 * @param {AudioNode} destination - The destination node
 * @returns {AudioNode} The output node after applying effects
 */
export function applyRadioEffect(audioContext, source, destination) {
  // Bandpass filter: 300Hz - 3000Hz (typical radio bandwidth)
  const lowpass = audioContext.createBiquadFilter();
  lowpass.type = "lowpass";
  lowpass.frequency.value = 3000;

  const highpass = audioContext.createBiquadFilter();
  highpass.type = "highpass";
  highpass.frequency.value = 300;

  // Light compression for radio-like dynamics
  const compressor = audioContext.createDynamicsCompressor();
  compressor.threshold.value = -20;
  compressor.knee.value = 10;
  compressor.ratio.value = 4;

  // Connect the chain
  source.connect(highpass);
  highpass.connect(lowpass);
  lowpass.connect(compressor);
  compressor.connect(destination);

  return compressor;
}

/**
 * Create a continuous subaudible tone to keep audio context unlocked on mobile
 * Uses a 67 Hz CTCSS tone at very low volume to remain effectively inaudible
 *
 * @param {AudioContext} audioContext - The audio context to use
 * @returns {Object} Object containing oscillator and gainNode for control
 */
export function createSubaudibleTone(audioContext) {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  // 67 Hz is a common CTCSS (Continuous Tone-Coded Squelch System) frequency
  oscillator.type = "sine";
  oscillator.frequency.value = 67;

  // Very low gain to make it essentially inaudible
  gainNode.gain.value = 0.001;

  // Connect oscillator to gain to destination
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  return { oscillator, gainNode };
}

/**
 * Start a subaudible tone to keep audio context unlocked
 * Must be called from a user gesture context
 *
 * @returns {Object} Object with { audioContext, oscillator, gainNode }
 */
export function startSubaudibleTone() {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  
  // Start immediately - don't wait for resume
  // The oscillator.start() call will implicitly resume the context on iOS
  const { oscillator, gainNode } = createSubaudibleTone(audioContext);
  oscillator.start();
  
  // Try to resume context asynchronously (but don't block on it)
  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(err => {
      console.warn('AudioContext resume failed:', err);
    });
  }

  return { audioContext, oscillator, gainNode };
}

/**
 * Stop and clean up a subaudible tone
 *
 * @param {Object} toneObject - Object containing audioContext, oscillator, and gainNode
 */
export function stopSubaudibleTone(toneObject) {
  if (toneObject) {
    const { audioContext, oscillator, gainNode } = toneObject;

    if (oscillator) {
      try {
        oscillator.stop();
        oscillator.disconnect();
      } catch (e) {
        // Oscillator may already be stopped
      }
    }

    if (gainNode) {
      gainNode.disconnect();
    }

    if (audioContext && audioContext.state !== "closed") {
      audioContext.close();
    }
  }
}

/**
 * Play a brief burst of subaudible tone (e.g., for PTT button press/release)
 * This provides audio feedback while keeping the context active
 *
 * @param {AudioContext} audioContext - The audio context
 * @param {number} duration - Duration in seconds (default 0.1)
 * @returns {Promise<void>} Resolves when burst completes
 */
export async function playSubaudibleToneBurst(audioContext, duration = 0.1) {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.type = "sine";
  oscillator.frequency.value = 67;

  // Slightly higher gain for the burst so it provides subtle feedback
  const now = audioContext.currentTime;
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(0.005, now + 0.01);
  gainNode.gain.linearRampToValueAtTime(0, now + duration);

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.start(now);
  oscillator.stop(now + duration);

  return new Promise((resolve) => {
    setTimeout(resolve, duration * 1000);
  });
}
